import { bitWiseAnd } from '../utility';
import { KaitaiStream } from './kaitai-stream';
const TAG = 'KaitaiStreamWriter';
export class KaitaiStreamWriter extends KaitaiStream {
    /**
     * @param arrayBuffer ArrayBuffer to read from.
     * @param byteOffset Offset from arrayBuffer beginning for the KaitaiStream.
     */
    constructor(arrayBuffer, byteOffset = 0, isBigEndian = true, options = {
        checkValueSize: false,
    }) {
        super(arrayBuffer, byteOffset, isBigEndian);
        this.options = options;
    }
    /**
     * Utility function to merge byte arrays
     * @param data
     */
    static mergeArrays(...data) {
        const totalSize = data.reduce((prev, curr) => prev + curr.length, 0);
        const stream = KaitaiStreamWriter.create(totalSize);
        for (const d of data) {
            stream.writeBytes(d);
        }
        return stream.toBytes;
    }
    static create(size) {
        return new KaitaiStreamWriter(new ArrayBuffer(size));
    }
    get toBytes() {
        const length = this.pos;
        let totalLength = this.pos;
        if (this._bitsLeft < 8) {
            totalLength++;
        }
        const result = new Uint8Array(totalLength);
        let i = 0;
        for (; i < length; i++) {
            result[i] = this._dataView.getUint8(i);
        }
        if (this._bitsLeft < 8) {
            result[totalLength - 1] = this._getBitsFromCurrentPosition();
        }
        return result;
    }
    /**
     *
     * @param value
     * @param type
     * @param size size in bits
     */
    write(value, type, size) {
        this._checkSize(size);
        const methodName = `write${type.toUpperCase()}`;
        if (type !== 'b') {
            size /= 8;
        }
        else {
            value = value === true ? 1 : value === false ? 0 : value;
        }
        if (!(methodName in this)) {
            throw new Error(`Invalid write type: type=${type}. Method name: ${methodName}`);
        }
        // console.debug(`write ${methodName} on ${size} bytes/bits `, value);
        return this[methodName](value, size);
    }
    /**
     * Append value on 1 byte
     * @param value
     */
    writeByte(value) {
        this._checkSize(1);
        this._dataView.setInt8(this.pos, value);
        this.pos++;
        return this;
    }
    writeU(value, byteSize) {
        this._checkSize(byteSize);
        if (this.options.checkValueSize) {
            const maxValue = Math.pow(2, byteSize * 8) - 1;
            if (value > maxValue) {
                // TODO custom error
                throw new Error(`Cannt write unsigned ${value} on ${byteSize} byte(s). Maximum value is ${maxValue}`);
            }
        }
        switch (byteSize) {
            case 4:
                this._dataView.setUint32(this.pos, value, this.isLittleEndian);
                break;
            case 2:
                this._dataView.setUint16(this.pos, value, this.isLittleEndian);
                break;
            case 1:
                this._dataView.setUint8(this.pos, value);
                break;
            default:
                throw new Error(`Unsupported unsigned size: ${byteSize}`);
        }
        this.pos += byteSize;
        return this;
    }
    writeF(value, byteSize) {
        this._checkSize(byteSize);
        switch (byteSize) {
            case 4:
                this._dataView.setFloat32(this.pos, value, this.isLittleEndian);
                break;
            case 8:
                this._dataView.setFloat64(this.pos, value, this.isLittleEndian);
                break;
            default:
                throw new Error(`Illegal float size: ${byteSize}`);
        }
        this.pos += byteSize;
        return this;
    }
    writeS(value, byteSize) {
        this._checkSize(byteSize);
        // TODO check size ?
        switch (byteSize) {
            case 4:
                this._dataView.setInt32(this.pos, value, this.isLittleEndian);
                break;
            case 2:
                this._dataView.setInt16(this.pos, value, this.isLittleEndian);
                break;
            case 1:
                this._dataView.setInt8(this.pos, value);
                break;
            default:
                throw new Error(`Illegal int size: ${byteSize}`);
        }
        this.pos += byteSize;
        return this;
    }
    /**
     * @deprecated flush bit calls are now useless
     * @param v
     */
    flushBits() { }
    _setBitsAtCurrentPosition(v) {
        this.dataView.setUint8(this.pos, v);
    }
    _setBit(value) {
        const bitMask = 1 << (this._bitsLeft - 1);
        let bits = this._getBitsFromCurrentPosition();
        if (value) {
            bits |= bitMask;
        }
        else {
            bits &= ~bitMask;
        }
        this._setBitsAtCurrentPosition(bits);
        this._decreaseBitsLeft(1);
        return this;
    }
    /**
     * Write bit per bit
     * @param v
     * @param n
     */
    writeB(v, n = 1) {
        v = v === true ? 1 : v === false ? 0 : v;
        for (let i = n - 1; i >= 0; i--) {
            const bitSetMask = Math.pow(2, i);
            this._setBit(bitWiseAnd(v, bitSetMask) ? true : false);
        }
        return this;
    }
    forwardBit() {
        this.forwardBits(1);
    }
    /**
     *
     * @param v true or false
     * @param n number of bits
     */
    writeBitsBoolean(v, n) {
        return this.writeB(v, n);
    }
    /**
     * Append given bytes to stream
     * @param data
     * @param size
     */
    writeBytes(data, size = data.length) {
        this._checkSize(size);
        // console.debug(`writeBytes ${size} bytes (remaining size: ${this.byteLeft})`)
        for (let i = 0; i < size; i++) {
            // console.debug(`writeBytes ${i} = ${data[i]} (remaining size: ${this.byteLeft})`)
            this._dataView.setInt8(this.pos, data[i]);
            this.pos++;
        }
        return this;
    }
    /**
     * Check that buffer size has enaugh room to add the byteSize bytes
     * Will resize buffer if not
     * @param byteSize
     */
    _checkSize(byteSize) {
        const availableSize = this.byteLeft;
        // console.debug(`_checkSize`, `request ${byteSize} bytes (available ${availableSize}; offset ${this.byteOffset})`)
        if (availableSize < byteSize) {
            const newSize = this._dataView.byteLength +
                (Math.max(this._dataView.byteLength, byteSize * 1.1) - this.byteLeft);
            this.resize(newSize);
            // debug(
            //     TAG,
            //     `_checkSize`,
            //     `Resizing: ${this.buffer.byteLength}. Offset: ${this.byteOffset}`
            // );
        }
    }
    resize(newSize) {
        const newBuffer = new Uint8Array(newSize);
        const currentData = this.toBytes;
        newBuffer.set(currentData);
        this.buffer = newBuffer.buffer;
    }
}
